import { Close } from '@mui/icons-material';
import {
    Box,
    Button,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    Stack,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router';

import DocumentTemplateForm from '../components/documentTemplateForm';
import DocumentTemplateTable from '../components/documentTemplateTable';
import DocumentPreview from 'components/v2/common/documentPreview';

import api from 'api';
import { ACTIONS } from 'constant';
import useModalObj from 'hooks/useModalObj';
import {
    organizationDocumentKeys,
    useOrganizationDocumentTemplates,
} from 'hooks/useOrganizationDocuments';
import useTranslate from 'hooks/useTranslate';
import { setAlert } from 'redux/alert';
import { ClientCompanyDocumentTemplate, DocumentInterface, DocumentPreviewModalData } from 'types';

interface Props {
    documents: DocumentInterface[];
    onClose: () => void;
}

const DocumentTemplate = ({ documents, onClose }: Props) => {
    const { id } = useParams();
    const dispatch = useDispatch();
    const queryClient = useQueryClient();
    const theme = useTheme();
    const mobile = useMediaQuery(theme.breakpoints.down('md'));
    const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const { translate } = useTranslate();
    const { modalObject, setModalObject, onResetModalObj } = useModalObj();
    const { data: documentTemplates = [], isLoading } = useOrganizationDocumentTemplates(id, {
        alertOnError: true,
    });

    const refreshTemplates = async () => {
        if (!id) return;
        await queryClient.invalidateQueries({ queryKey: organizationDocumentKeys.templates(id) });
    };

    const handleDocumentAssignment = async (data: ClientCompanyDocumentTemplate) => {
        try {
            if (!id) return;

            const response = await api.clientServiceProviders.assignDocument(+id, {
                document_template_id: data.id,
            });
            if (response.data) {
                await refreshTemplates();
                dispatch(setAlert({ message: 'saveChanges', type: 'success' }));
            }
        } catch {
            dispatch(setAlert({ message: 'somethingWentWrong', type: 'error' }));
        }
    };

    const handleAction = (action: ACTIONS, data: ClientCompanyDocumentTemplate | null) => {
        switch (action) {
            case ACTIONS.SELECT:
                handleDocumentAssignment(data as ClientCompanyDocumentTemplate);
                break;

            case ACTIONS.CREATE:
                setModalObject((prev) => ({
                    ...prev,
                    action,
                    data,
                    title: 'addDocumentTemplate',
                }));
                break;

            case ACTIONS.EDIT:
                setModalObject((prev) => ({
                    ...prev,
                    action,
                    data,
                    title: 'editDocumentTemplate',
                }));
                break;

            case ACTIONS.PREVIEW_FILE:
                handleFilePreview(data as ClientCompanyDocumentTemplate, true);
                break;

            case ACTIONS.PREVIEW_SAMPLE:
                handleFilePreview(data as ClientCompanyDocumentTemplate, false);

                break;

            default:
                break;
        }
    };

    const handleFilePreview = async (data?: ClientCompanyDocumentTemplate, isPreview = false) => {
        if (!id || !data) {
            return;
        }

        try {
            setModalObject((prev) => ({ ...prev, disabled: true }));
            let response;
            if (isPreview) {
                response = await api.documentTemplates.getDocumentTemplatePreviewUrl(data.id);
            } else {
                response = response =
                    await api.documentTemplates.getDocumentTemplateSamplePreviewUrl(data.id);
            }

            const url = response.data.url;
            const storedName = (isPreview ? data.template_uri : data.sample_uri) || '';
            const storedBase = storedName.split('/').pop()?.split('?')[0] || '';
            const fileName = data.name_en || data.name_fr || storedBase;

            setModalObject((prev) => ({
                ...prev,
                action: ACTIONS.PREVIEW_FILE,
                title: 'templatePreview',
                data: {
                    fileUrl: url,
                    fileName,
                },
            }));
        } catch {
            setModalObject((prev) => ({ ...prev, disabled: false }));
            dispatch(setAlert({ message: 'somethingWentWrong', type: 'error' }));
        }
    };

    const handleClose = () => {
        void refreshTemplates();
        onResetModalObj();
    };

    return (
        <>
            <Dialog
                open
                fullWidth
                maxWidth="lg"
                fullScreen={fullScreen}
                scroll="paper"
                slotProps={{
                    paper: {
                        sx: { maxHeight: 'calc(100% - 48px)' },
                    },
                }}
            >
                <DialogTitle
                    component="div"
                    sx={{
                        display: 'flex',
                        flexDirection: mobile ? 'column' : 'row',
                        alignItems: mobile ? 'stretch' : 'center',
                        justifyContent: 'space-between',
                        gap: 1,
                        minWidth: 0,
                    }}
                >
                    <Stack
                        direction="row"
                        spacing={1}
                        sx={{ alignItems: 'center', minWidth: 0, flex: mobile ? undefined : 1 }}
                    >
                        <Box
                            component="span"
                            sx={{
                                minWidth: 0,
                                flex: 1,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {translate('documentTemplates')}
                        </Box>
                        {mobile ? (
                            <IconButton
                                size="small"
                                aria-label={translate('close')}
                                onClick={onClose}
                            >
                                <Close fontSize="inherit" />
                            </IconButton>
                        ) : null}
                    </Stack>
                    <Stack
                        direction="row"
                        sx={{
                            gap: 1,
                            flexShrink: 0,
                            alignItems: 'center',
                            width: mobile ? '100%' : undefined,
                        }}
                    >
                        <Button
                            variant="outlined"
                            size="small"
                            fullWidth={mobile}
                            onClick={() => handleAction(ACTIONS.CREATE, null)}
                            sx={{ textTransform: 'none' }}
                        >
                            {`${translate('addDocument')} ${translate('template')}`}
                        </Button>
                        {mobile ? null : (
                            <IconButton
                                size="small"
                                aria-label={translate('close')}
                                onClick={onClose}
                            >
                                <Close fontSize="inherit" />
                            </IconButton>
                        )}
                    </Stack>
                </DialogTitle>

                <DialogContent>
                    <DocumentTemplateTable
                        loading={isLoading}
                        documentTemplates={documentTemplates}
                        handleAction={handleAction}
                    />
                </DialogContent>
            </Dialog>

            {[ACTIONS.CREATE, ACTIONS.EDIT].includes(modalObject.action as ACTIONS) && (
                <DocumentTemplateForm
                    initialData={modalObject.data as ClientCompanyDocumentTemplate}
                    title={modalObject.title}
                    onClose={handleClose}
                />
            )}

            {(modalObject.action === ACTIONS.PREVIEW_FILE ||
                modalObject.action === ACTIONS.PREVIEW_SAMPLE) && (
                <DocumentPreview
                    open
                    mobile={mobile}
                    onClose={onResetModalObj}
                    title={translate(modalObject.title)}
                    fileUrl={(modalObject.data as DocumentPreviewModalData)?.fileUrl}
                    fileName={(modalObject.data as DocumentPreviewModalData)?.fileName}
                />
            )}
        </>
    );
};

export default DocumentTemplate;
